import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { api } from '@/App';
import getUserFromStorage from '@/utils/userHelper';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';
import { 
  Globe, 
  Settings, 
  CreditCard, 
  Save, 
  Loader2,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Eye,
  Building,
  Bed,
  MapPin,
  Search,
  ToggleLeft,
  ToggleRight,
  Edit,
  Tag,
  Percent,
  Calendar,
  IndianRupee,
  Trash2,
  Image,
  Upload,
  X,
  BarChart3,
  TrendingDown,
  TrendingUp,
  Navigation,
  Plane,
  Train,
  Bus,
  Hospital,
  ShoppingBag,
  Utensils,
  Landmark,
  Map
} from 'lucide-react';

const PropertyListing = () => {
  const user = getUserFromStorage();
  const { openTab } = useTabs();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [togglingProperty, setTogglingProperty] = useState(null);
  const [loadingListing, setLoadingListing] = useState(false);
  const [uploadingHotelPhoto, setUploadingHotelPhoto] = useState(false);
  const [uploadingRoomPhoto, setUploadingRoomPhoto] = useState(null);
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingProperty, setPricingProperty] = useState(null);
  const [pricingData, setPricingData] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  
  // Room type pricing rules for selected property (Configure dialog)
  const [propertyPricingRules, setPropertyPricingRules] = useState([]);
  const [loadingPropertyPricing, setLoadingPropertyPricing] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);
  
  const [listing, setListing] = useState({
    listing_enabled: true,
    booking_mode: 'use_default',
    payment_mode: 'use_default',
    advance_percentage: 20,
    featured: false,
    listing_title: '',
    listing_description: '',
    listing_photos: [],
    amenities: [],
    check_in_time: '14:00',
    check_out_time: '11:00',
    subdomain: '',
    custom_domain: '',
    room_allocations: {},
    discount_offers: [],
    room_types: [],
    // Location fields
    location: {
      latitude: null,
      longitude: null,
      address: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      google_maps_url: ''
    },
    nearby_places: [] // Array of { name, type, distance, distance_unit }
  });
  
  const [hotelId, setHotelId] = useState(null);

  useEffect(() => {
    fetchAmenities();
    fetchRoomTypesAndBedTypes();
    if (user?.role === 'super_admin') {
      fetchAllProperties();
    } else if (user?.role === 'company_admin') {
      fetchCompanyProperties();
    } else if (user?.role === 'hotel_admin') {
      setHotelId(user.hotel_id);
      fetchListingForHotelAdmin(user.hotel_id);
    }
  }, [user?.role]);

  const fetchAmenities = async () => {
    try {
      const response = await api.get('/amenities');
      setAvailableAmenities(response.data.filter(a => a.is_active !== false));
    } catch (err) {
      console.error('Failed to fetch amenities:', err);
    }
  };

  const fetchRoomTypesAndBedTypes = async () => {
    try {
      const [rtRes, btRes] = await Promise.all([
        api.get('/room-types'),
        api.get('/bed-types')
      ]);
      setRoomTypes(rtRes.data || []);
      setBedTypes(btRes.data || []);
    } catch (err) {
      console.error('Failed to fetch room/bed types:', err);
    }
  };

  const fetchPropertyPricingRules = async (hotelId) => {
    setLoadingPropertyPricing(true);
    try {
      const response = await api.get(`/admin/hotel/${hotelId}/pricing-rules`);
      setPropertyPricingRules(response.data || []);
    } catch (err) {
      console.error('Failed to fetch pricing rules:', err);
      setPropertyPricingRules([]);
    } finally {
      setLoadingPropertyPricing(false);
    }
  };

  const handleUpdatePricingRule = async (ruleId, updates) => {
    try {
      await api.put(`/admin/hotel/${selectedProperty.id}/pricing-rules/${ruleId}`, updates);
      toast.success('Pricing rule updated');
      fetchPropertyPricingRules(selectedProperty.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const handleCreatePricingRule = async (ruleData) => {
    try {
      await api.post(`/admin/hotel/${selectedProperty.id}/pricing-rules`, ruleData);
      toast.success('Pricing rule created');
      fetchPropertyPricingRules(selectedProperty.id);
      fetchAllProperties(); // Refresh to update room count
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create');
    }
  };

  const handleDeletePricingRule = async (ruleId) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    try {
      await api.delete(`/admin/hotel/${selectedProperty.id}/pricing-rules/${ruleId}`);
      toast.success('Pricing rule deleted');
      fetchPropertyPricingRules(selectedProperty.id);
      fetchAllProperties(); // Refresh to update room count
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const fetchAllProperties = async () => {
    try {
      const response = await api.get('/super-admin/hotels');
      const hotelsData = response.data;
      
      const propertiesWithListings = await Promise.all(
        hotelsData.map(async (hotel) => {
          try {
            const [listingRes, discountRes] = await Promise.all([
              api.get(`/portal/admin/listing/${hotel.id}`),
              api.get(`/dynamic-pricing/max-discount/${hotel.id}`).catch(() => ({ data: { max_portal_discount: 10, pricing_source: 'mock' } }))
            ]);
            const totalRooms = listingRes.data.room_types?.reduce((sum, rt) => sum + rt.total_rooms, 0) || 0;
            
            return {
              ...hotel,
              listing: listingRes.data,
              total_rooms: totalRooms,
              max_portal_discount: discountRes.data.max_portal_discount || 10,
              competitor_undercut_percent: discountRes.data.competitor_undercut_percent || 5,
              pricing_source: discountRes.data.pricing_source || 'mock'
            };
          } catch (error) {
            return { ...hotel, listing: { listing_enabled: false }, total_rooms: 0, max_portal_discount: 10, competitor_undercut_percent: 5, pricing_source: 'mock' };
          }
        })
      );
      
      setProperties(propertiesWithListings);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyProperties = async () => {
    try {
      const response = await api.get('/user/hotels');
      const hotelsData = response.data;
      
      const propertiesWithListings = await Promise.all(
        hotelsData.map(async (hotel) => {
          try {
            const listingRes = await api.get(`/portal/admin/listing/${hotel.id}`);
            const totalRooms = listingRes.data.room_types?.reduce((sum, rt) => sum + rt.total_rooms, 0) || 0;
            const portalRooms = listingRes.data.room_types?.reduce((sum, rt) => {
              const allocs = rt.allocations || [];
              if (allocs.length === 0) return sum + rt.total_rooms;
              return sum + Math.max(...allocs.map(a => a.allocated_rooms || 0), 0);
            }, 0) || 0;
            
            return { ...hotel, listing: listingRes.data, total_rooms: totalRooms, portal_rooms: portalRooms };
          } catch (error) {
            return { ...hotel, listing: { listing_enabled: false }, total_rooms: 0, portal_rooms: 0 };
          }
        })
      );
      
      setProperties(propertiesWithListings);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchListingForHotelAdmin = async (id) => {
    try {
      const response = await api.get(`/portal/admin/listing/${id}`);
      setListing(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePropertyListing = async (property) => {
    setTogglingProperty(property.id);
    try {
      const newStatus = !property.listing?.listing_enabled;
      await api.put(`/portal/admin/listing/${property.id}`, { listing_enabled: newStatus });
      
      setProperties(prev => prev.map(p => 
        p.id === property.id 
          ? { ...p, listing: { ...p.listing, listing_enabled: newStatus } }
          : p
      ));
      
      toast.success(`${property.name} ${newStatus ? 'activated' : 'deactivated'} on portal`);
    } catch (error) {
      toast.error('Failed to update property status');
    } finally {
      setTogglingProperty(null);
    }
  };

  const openEditDialog = async (property) => {
    setSelectedProperty(property);
    setLoadingListing(true);
    setEditDialogOpen(true);
    
    try {
      const response = await api.get(`/portal/admin/listing/${property.id}`);
      setListing({
        listing_enabled: response.data.listing_enabled ?? true,
        booking_mode: response.data.booking_mode || 'use_default',
        payment_mode: response.data.payment_mode || 'use_default',
        advance_percentage: response.data.advance_percentage || 20,
        featured: response.data.featured || false,
        listing_title: response.data.listing_title || '',
        listing_description: response.data.listing_description || '',
        listing_photos: response.data.listing_photos || [],
        amenities: response.data.amenities || [],
        check_in_time: response.data.check_in_time || '14:00',
        check_out_time: response.data.check_out_time || '11:00',
        subdomain: response.data.subdomain || '',
        custom_domain: response.data.custom_domain || '',
        room_allocations: response.data.room_allocations || {},
        discount_offers: response.data.discount_offers || [],
        room_types: response.data.room_types || [],
        // Location fields
        location: response.data.location || {
          latitude: null,
          longitude: null,
          address: '',
          city: '',
          state: '',
          country: '',
          postal_code: '',
          google_maps_url: ''
        },
        nearby_places: response.data.nearby_places || []
      });
      // Also fetch pricing rules for this property
      fetchPropertyPricingRules(property.id);
    } catch (error) {
      toast.error('Failed to load listing data');
    } finally {
      setLoadingListing(false);
    }
  };

  // Photo management functions
  const uploadPhoto = async (file, photoType, entityId) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ photo_type: photoType });
    if (entityId) params.append('entity_id', entityId);
    const response = await api.post(`/upload/portal-photo?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.photo_url;
  };

  const handleHotelPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const propertyId = selectedProperty?.id || hotelId;
    setUploadingHotelPhoto(true);
    try {
      for (const file of files) {
        const url = await uploadPhoto(file, 'hotel', propertyId);
        setListing(prev => ({
          ...prev,
          listing_photos: [...(prev.listing_photos || []), url]
        }));
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingHotelPhoto(false);
      e.target.value = '';
    }
  };

  const removeHotelPhoto = (index) => {
    setListing(prev => ({
      ...prev,
      listing_photos: prev.listing_photos.filter((_, i) => i !== index)
    }));
  };

  const handleRoomPhotoUpload = async (e, roomTypeId) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingRoomPhoto(roomTypeId);
    try {
      for (const file of files) {
        const url = await uploadPhoto(file, 'room', roomTypeId);
        setListing(prev => {
          const updatedRoomTypes = prev.room_types.map(rt => {
            if (rt.id === roomTypeId) {
              return { ...rt, photos: [...(rt.photos || []), url] };
            }
            return rt;
          });
          return { ...prev, room_types: updatedRoomTypes };
        });
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingRoomPhoto(null);
      e.target.value = '';
    }
  };

  const removeRoomTypePhoto = (roomTypeId, photoIndex) => {
    setListing(prev => {
      const updatedRoomTypes = prev.room_types.map(rt => {
        if (rt.id === roomTypeId) {
          return { ...rt, photos: rt.photos.filter((_, i) => i !== photoIndex) };
        }
        return rt;
      });
      return { ...prev, room_types: updatedRoomTypes };
    });
  };

  // Amenity management functions
  const toggleHotelAmenity = (amenityName) => {
    setListing(prev => {
      const current = prev.amenities || [];
      const updated = current.includes(amenityName)
        ? current.filter(a => a !== amenityName)
        : [...current, amenityName];
      return { ...prev, amenities: updated };
    });
  };

  const toggleRoomAmenity = (roomTypeId, amenityName) => {
    setListing(prev => {
      const updatedRoomTypes = prev.room_types.map(rt => {
        if (rt.id === roomTypeId) {
          const current = rt.amenities || [];
          const updated = current.includes(amenityName)
            ? current.filter(a => a !== amenityName)
            : [...current, amenityName];
          return { ...rt, amenities: updated };
        }
        return rt;
      });
      return { ...prev, room_types: updatedRoomTypes };
    });
  };

  // Add new allocation period for a room type
  const addAllocationPeriod = (roomTypeId) => {
    const rt = listing.room_types.find(r => r.id === roomTypeId);
    if (!rt) return;
    
    const newAllocation = {
      id: Date.now().toString(),
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      allocated_rooms: rt.total_rooms,
      portal_price: rt.base_price,
      discount_percent: 0
    };
    
    setListing(prev => {
      const allocations = { ...prev.room_allocations };
      if (!allocations[roomTypeId]) {
        allocations[roomTypeId] = [];
      }
      allocations[roomTypeId] = [...allocations[roomTypeId], newAllocation];
      
      // Update room_types for display
      const updatedRoomTypes = prev.room_types.map(r => {
        if (r.id === roomTypeId) {
          return { ...r, allocations: allocations[roomTypeId] };
        }
        return r;
      });
      
      return { ...prev, room_allocations: allocations, room_types: updatedRoomTypes };
    });
  };

  // Update allocation period
  const updateAllocationPeriod = (roomTypeId, allocationId, field, value) => {
    setListing(prev => {
      const allocations = { ...prev.room_allocations };
      if (!allocations[roomTypeId]) return prev;
      
      allocations[roomTypeId] = allocations[roomTypeId].map(alloc => 
        alloc.id === allocationId ? { ...alloc, [field]: value } : alloc
      );
      
      const updatedRoomTypes = prev.room_types.map(r => {
        if (r.id === roomTypeId) {
          return { ...r, allocations: allocations[roomTypeId] };
        }
        return r;
      });
      
      return { ...prev, room_allocations: allocations, room_types: updatedRoomTypes };
    });
  };

  // Remove allocation period
  const removeAllocationPeriod = (roomTypeId, allocationId) => {
    setListing(prev => {
      const allocations = { ...prev.room_allocations };
      if (!allocations[roomTypeId]) return prev;
      
      allocations[roomTypeId] = allocations[roomTypeId].filter(a => a.id !== allocationId);
      
      const updatedRoomTypes = prev.room_types.map(r => {
        if (r.id === roomTypeId) {
          return { ...r, allocations: allocations[roomTypeId] };
        }
        return r;
      });
      
      return { ...prev, room_allocations: allocations, room_types: updatedRoomTypes };
    });
  };

  const addDiscountOffer = () => {
    const newOffer = {
      id: Date.now().toString(),
      name: '',
      discount_percent: 10,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      min_nights: 1,
      room_type_ids: []
    };
    setListing(prev => ({
      ...prev,
      discount_offers: [...(prev.discount_offers || []), newOffer]
    }));
  };

  const updateDiscountOffer = (offerId, field, value) => {
    setListing(prev => ({
      ...prev,
      discount_offers: prev.discount_offers.map(offer => 
        offer.id === offerId ? { ...offer, [field]: value } : offer
      )
    }));
  };

  const removeDiscountOffer = (offerId) => {
    setListing(prev => ({
      ...prev,
      discount_offers: prev.discount_offers.filter(offer => offer.id !== offerId)
    }));
  };

  const openPricingAnalysis = async (property) => {
    setPricingProperty(property);
    setPricingDialogOpen(true);
    setLoadingPricing(true);
    setPricingData([]);
    
    try {
      const listingRes = await api.get(`/portal/admin/listing/${property.id}`);
      const roomTypes = listingRes.data.room_types || [];
      const checkIn = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
      const checkOut = new Date(Date.now() + 8 * 86400000).toISOString().split('T')[0];
      
      const results = await Promise.all(
        roomTypes.map(async (rt) => {
          try {
            const res = await api.post('/dynamic-pricing/lookup', {
              hotel_name: property.name || 'Hotel',
              city: property.address?.city || 'India',
              room_type: rt.name,
              check_in: checkIn,
              check_out: checkOut,
              base_price: rt.base_price || 0
            });
            return { roomType: rt.name, basePrice: rt.base_price, ...res.data };
          } catch {
            return { roomType: rt.name, basePrice: rt.base_price, competitors: [], error: true };
          }
        })
      );
      setPricingData(results);
    } catch (err) {
      toast.error('Failed to fetch pricing data');
    } finally {
      setLoadingPricing(false);
    }
  };

  const handleSaveListing = async () => {
    if (!selectedProperty && !hotelId) return;
    
    const propertyId = selectedProperty?.id || hotelId;
    
    setSaving(true);
    try {
      // Save listing data (excluding room_types which is read-only for listings)
      const { room_types, ...saveData } = listing;
      await api.put(`/portal/admin/listing/${propertyId}`, saveData);
      
      // Save room type photos and amenities separately
      for (const rt of room_types) {
        const rtUpdate = {};
        if (rt.photos && rt.photos.length > 0) rtUpdate.photos = rt.photos;
        if (rt.amenities) rtUpdate.amenities = rt.amenities;
        if (Object.keys(rtUpdate).length > 0) {
          try {
            await api.put(`/room-types/${rt.id}`, rtUpdate);
          } catch (err) {
            console.error(`Failed to save data for room type ${rt.name}:`, err);
          }
        }
      }
      
      if (selectedProperty) {
        setProperties(prev => prev.map(p => 
          p.id === propertyId ? { ...p, listing: { ...listing } } : p
        ));
      }
      
      toast.success('Listing settings saved successfully');
      if (editDialogOpen) setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const filteredProperties = properties.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render Room Allocation with Date Periods
  const renderRoomAllocationTab = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-700">
          <strong>Date-based Room Allocation:</strong> Create multiple allocation periods with different room counts and pricing for each date range.
        </p>
      </div>

      {listing.room_types?.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Bed className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">No room types found for this property</p>
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-2">
          {listing.room_types?.map((rt) => (
            <AccordionItem key={rt.id} value={rt.id} className="border rounded-lg">
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center justify-between w-full mr-4">
                  <div className="flex items-center gap-3">
                    <Bed className="h-5 w-5 text-orange-500" />
                    <div className="text-left">
                      <p className="font-medium">{rt.name}</p>
                      <p className="text-xs text-gray-500">Base: ₹{rt.base_price?.toLocaleString()} | Max {rt.max_occupancy} guests</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{rt.total_rooms} total</Badge>
                    <Badge className="bg-blue-100 text-blue-700">
                      {(rt.allocations?.length || 0)} periods
                    </Badge>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3">
                  {/* Allocation periods */}
                  {(!rt.allocations || rt.allocations.length === 0) ? (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 text-sm">No allocation periods defined</p>
                      <p className="text-xs text-gray-400">All {rt.total_rooms} rooms available at base price by default</p>
                    </div>
                  ) : (
                    rt.allocations.map((alloc, idx) => (
                      <Card key={alloc.id || idx} className="bg-gray-50">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <Badge variant="outline" className="bg-white">
                              <Calendar className="h-3 w-3 mr-1" />
                              Period {idx + 1}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => removeAllocationPeriod(rt.id, alloc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-5 gap-3">
                            <div>
                              <Label className="text-xs">From Date</Label>
                              <Input
                                type="date"
                                value={alloc.from_date || ''}
                                onChange={(e) => updateAllocationPeriod(rt.id, alloc.id, 'from_date', e.target.value)}
                                className="mt-1 h-9 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">To Date</Label>
                              <Input
                                type="date"
                                value={alloc.to_date || ''}
                                onChange={(e) => updateAllocationPeriod(rt.id, alloc.id, 'to_date', e.target.value)}
                                className="mt-1 h-9 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Rooms (max {rt.total_rooms})</Label>
                              <Input
                                type="number"
                                min={0}
                                max={rt.total_rooms}
                                value={alloc.allocated_rooms ?? rt.total_rooms}
                                onChange={(e) => updateAllocationPeriod(rt.id, alloc.id, 'allocated_rooms', parseInt(e.target.value) || 0)}
                                className="mt-1 h-9 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Portal Price (₹)</Label>
                              <Input
                                type="number"
                                min={0}
                                value={alloc.portal_price || ''}
                                onChange={(e) => updateAllocationPeriod(rt.id, alloc.id, 'portal_price', parseFloat(e.target.value) || null)}
                                placeholder={rt.base_price?.toString()}
                                className="mt-1 h-9 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Discount %</Label>
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                value={alloc.discount_percent || ''}
                                onChange={(e) => updateAllocationPeriod(rt.id, alloc.id, 'discount_percent', parseInt(e.target.value) || 0)}
                                placeholder="0"
                                className="mt-1 h-9 text-sm"
                              />
                            </div>
                          </div>
                          
                          {/* Show final price */}
                          <div className="mt-2 text-right text-sm">
                            <span className="text-gray-500">Final Price: </span>
                            <span className="font-semibold text-green-600">
                              ₹{((alloc.portal_price || rt.base_price) * (1 - (alloc.discount_percent || 0) / 100)).toLocaleString()}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                  
                  {/* Add period button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addAllocationPeriod(rt.id)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Allocation Period
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );

  // Render Configure Dialog Content
  const renderConfigureDialogContent = () => (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid w-full grid-cols-7">
        <TabsTrigger value="basic" className="text-xs">
          <Settings className="h-3 w-3 mr-1" />
          Basic Info
        </TabsTrigger>
        <TabsTrigger value="location" className="text-xs">
          <MapPin className="h-3 w-3 mr-1" />
          Location
        </TabsTrigger>
        <TabsTrigger value="amenities" className="text-xs">
          <CheckCircle className="h-3 w-3 mr-1" />
          Amenities
        </TabsTrigger>
        <TabsTrigger value="photos" className="text-xs">
          <Image className="h-3 w-3 mr-1" />
          Photos
        </TabsTrigger>
        <TabsTrigger value="rooms" className="text-xs">
          <Bed className="h-3 w-3 mr-1" />
          Rooms
        </TabsTrigger>
        <TabsTrigger value="pricing" className="text-xs">
          <IndianRupee className="h-3 w-3 mr-1" />
          Room Pricing
        </TabsTrigger>
        <TabsTrigger value="discounts" className="text-xs">
          <Tag className="h-3 w-3 mr-1" />
          Discounts
        </TabsTrigger>
      </TabsList>

      {/* Basic Info Tab */}
      <TabsContent value="basic" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="listing_title">Display Name</Label>
            <Input
              id="listing_title"
              value={listing.listing_title || ''}
              onChange={(e) => setListing({ ...listing, listing_title: e.target.value })}
              placeholder={selectedProperty?.name || 'Property name'}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="subdomain">Custom Subdomain</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="subdomain"
                value={listing.subdomain || ''}
                onChange={(e) => setListing({ ...listing, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="your-hotel"
              />
              <span className="text-gray-500 text-sm whitespace-nowrap">.portal.com</span>
            </div>
          </div>
        </div>
        
        {/* Custom Domain Configuration */}
        <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="custom_domain" className="text-blue-800 font-medium">Custom Domain (White Label)</Label>
              <p className="text-xs text-blue-600 mb-2">Point your own domain to this hotel's portal</p>
              <Input
                id="custom_domain"
                value={listing.custom_domain || ''}
                onChange={(e) => setListing({ ...listing, custom_domain: e.target.value.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '') })}
                placeholder="yourdomain.com"
                className="bg-white"
              />
              {listing.custom_domain && (
                <div className="mt-2 p-2 bg-white rounded border text-xs">
                  <p className="font-medium text-gray-700 mb-1">DNS Configuration Required:</p>
                  <p className="text-gray-600">Add a CNAME record pointing <span className="font-mono bg-gray-100 px-1">{listing.custom_domain}</span> to <span className="font-mono bg-gray-100 px-1">portal.emergentagent.com</span></p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <Label htmlFor="listing_description">Description</Label>
          <Textarea
            id="listing_description"
            value={listing.listing_description || ''}
            onChange={(e) => setListing({ ...listing, listing_description: e.target.value })}
            placeholder="Describe this property..."
            className="mt-1"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Booking Mode</Label>
            <Select
              value={listing.booking_mode || 'use_default'}
              onValueChange={(value) => setListing({ ...listing, booking_mode: value })}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="use_default">Use Platform Default</SelectItem>
                <SelectItem value="instant">Instant Confirmation</SelectItem>
                <SelectItem value="request">Request (Approval Required)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment Mode</Label>
            <Select
              value={listing.payment_mode || 'use_default'}
              onValueChange={(value) => setListing({ ...listing, payment_mode: value })}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="use_default">Use Platform Default</SelectItem>
                <SelectItem value="online_full">Full Payment Online</SelectItem>
                <SelectItem value="online_advance">Advance Payment Online</SelectItem>
                <SelectItem value="pay_at_hotel">Pay at Hotel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Check-in Time</Label>
            <Input
              type="time"
              value={listing.check_in_time || '14:00'}
              onChange={(e) => setListing({ ...listing, check_in_time: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Check-out Time</Label>
            <Input
              type="time"
              value={listing.check_out_time || '11:00'}
              onChange={(e) => setListing({ ...listing, check_out_time: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-orange-500" />
            <div>
              <Label className="font-medium">Featured Property</Label>
              <p className="text-xs text-gray-500">Show prominently on homepage</p>
            </div>
          </div>
          <Switch
            checked={listing.featured}
            onCheckedChange={(checked) => setListing({ ...listing, featured: checked })}
          />
        </div>
      </TabsContent>

      {/* Location Tab */}
      <TabsContent value="location" className="space-y-6 mt-4">
        {/* Google Maps Integration */}
        <div className="space-y-4">
          <div>
            <h3 className="font-medium flex items-center gap-2">
              <Map className="h-4 w-4 text-orange-500" />
              Property Location
            </h3>
            <p className="text-sm text-gray-500">Set your property's exact location on the map</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Latitude</Label>
              <Input
                type="number"
                step="any"
                value={listing.location?.latitude || ''}
                onChange={(e) => setListing({
                  ...listing,
                  location: { ...listing.location, latitude: parseFloat(e.target.value) || null }
                })}
                placeholder="e.g., 12.9716"
              />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input
                type="number"
                step="any"
                value={listing.location?.longitude || ''}
                onChange={(e) => setListing({
                  ...listing,
                  location: { ...listing.location, longitude: parseFloat(e.target.value) || null }
                })}
                placeholder="e.g., 77.5946"
              />
            </div>
          </div>

          <div>
            <Label>Full Address</Label>
            <Textarea
              value={listing.location?.address || ''}
              onChange={(e) => setListing({
                ...listing,
                location: { ...listing.location, address: e.target.value }
              })}
              placeholder="Enter complete property address"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>City</Label>
              <Input
                value={listing.location?.city || ''}
                onChange={(e) => setListing({
                  ...listing,
                  location: { ...listing.location, city: e.target.value }
                })}
                placeholder="City name"
              />
            </div>
            <div>
              <Label>State/Province</Label>
              <Input
                value={listing.location?.state || ''}
                onChange={(e) => setListing({
                  ...listing,
                  location: { ...listing.location, state: e.target.value }
                })}
                placeholder="State or Province"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Country</Label>
              <Input
                value={listing.location?.country || ''}
                onChange={(e) => setListing({
                  ...listing,
                  location: { ...listing.location, country: e.target.value }
                })}
                placeholder="Country"
              />
            </div>
            <div>
              <Label>Postal/ZIP Code</Label>
              <Input
                value={listing.location?.postal_code || ''}
                onChange={(e) => setListing({
                  ...listing,
                  location: { ...listing.location, postal_code: e.target.value }
                })}
                placeholder="Postal code"
              />
            </div>
          </div>

          <div>
            <Label>Google Maps URL</Label>
            <Input
              value={listing.location?.google_maps_url || ''}
              onChange={(e) => setListing({
                ...listing,
                location: { ...listing.location, google_maps_url: e.target.value }
              })}
              placeholder="Paste Google Maps link here"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tip: Go to Google Maps, find your property, click "Share" and paste the link here
            </p>
          </div>

          {/* Map Preview */}
          {listing.location?.latitude && listing.location?.longitude && (
            <div className="border rounded-lg overflow-hidden">
              <iframe
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${listing.location.latitude},${listing.location.longitude}&zoom=15`}
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Property Location"
              />
            </div>
          )}

          {!listing.location?.latitude && !listing.location?.longitude && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
              <MapPin className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Enter coordinates to see map preview</p>
              <p className="text-xs text-gray-400 mt-1">You can get coordinates from Google Maps</p>
            </div>
          )}
        </div>

        {/* Nearby Places */}
        <div className="border-t pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium flex items-center gap-2">
                <Navigation className="h-4 w-4 text-orange-500" />
                Nearby Places
              </h3>
              <p className="text-sm text-gray-500">Add nearby landmarks, transport hubs, and attractions</p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => setListing({
                ...listing,
                nearby_places: [
                  ...(listing.nearby_places || []),
                  { name: '', type: 'landmark', distance: '', distance_unit: 'km', latitude: null, longitude: null, google_maps_url: '' }
                ]
              })}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Place
            </Button>
          </div>

          {(!listing.nearby_places || listing.nearby_places.length === 0) ? (
            <div className="border-2 border-dashed rounded-lg p-6 text-center bg-gray-50">
              <Landmark className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No nearby places added</p>
              <p className="text-xs text-gray-400">Add airports, stations, attractions, etc.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {listing.nearby_places.map((place, index) => (
                <div key={index} className="border rounded-lg bg-white overflow-hidden">
                  {/* Main Row */}
                  <div className="flex items-center gap-3 p-3">
                    <Select
                      value={place.type}
                      onValueChange={(value) => {
                        const updated = [...listing.nearby_places];
                        updated[index] = { ...place, type: value };
                        setListing({ ...listing, nearby_places: updated });
                      }}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="airport">
                          <span className="flex items-center gap-2"><Plane className="h-3 w-3" /> Airport</span>
                        </SelectItem>
                        <SelectItem value="railway">
                          <span className="flex items-center gap-2"><Train className="h-3 w-3" /> Railway</span>
                        </SelectItem>
                        <SelectItem value="bus">
                          <span className="flex items-center gap-2"><Bus className="h-3 w-3" /> Bus Station</span>
                        </SelectItem>
                        <SelectItem value="hospital">
                          <span className="flex items-center gap-2"><Hospital className="h-3 w-3" /> Hospital</span>
                        </SelectItem>
                        <SelectItem value="shopping">
                          <span className="flex items-center gap-2"><ShoppingBag className="h-3 w-3" /> Shopping</span>
                        </SelectItem>
                        <SelectItem value="restaurant">
                          <span className="flex items-center gap-2"><Utensils className="h-3 w-3" /> Restaurant</span>
                        </SelectItem>
                        <SelectItem value="landmark">
                          <span className="flex items-center gap-2"><Landmark className="h-3 w-3" /> Landmark</span>
                        </SelectItem>
                        <SelectItem value="beach">
                          <span className="flex items-center gap-2"><Globe className="h-3 w-3" /> Beach</span>
                        </SelectItem>
                        <SelectItem value="other">
                          <span className="flex items-center gap-2"><MapPin className="h-3 w-3" /> Other</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Input
                      className="flex-1"
                      placeholder="Place name"
                      value={place.name}
                      onChange={(e) => {
                        const updated = [...listing.nearby_places];
                        updated[index] = { ...place, name: e.target.value };
                        setListing({ ...listing, nearby_places: updated });
                      }}
                    />

                    <Input
                      className="w-16"
                      type="number"
                      step="0.1"
                      placeholder="Dist"
                      value={place.distance}
                      onChange={(e) => {
                        const updated = [...listing.nearby_places];
                        updated[index] = { ...place, distance: e.target.value };
                        setListing({ ...listing, nearby_places: updated });
                      }}
                    />

                    <Select
                      value={place.distance_unit}
                      onValueChange={(value) => {
                        const updated = [...listing.nearby_places];
                        updated[index] = { ...place, distance_unit: value };
                        setListing({ ...listing, nearby_places: updated });
                      }}
                    >
                      <SelectTrigger className="w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="km">km</SelectItem>
                        <SelectItem value="m">m</SelectItem>
                        <SelectItem value="mi">mi</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        const updated = listing.nearby_places.filter((_, i) => i !== index);
                        setListing({ ...listing, nearby_places: updated });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Location Row */}
                  <div className="border-t bg-gray-50 p-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-gray-500">Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="e.g., 12.9716"
                          value={place.latitude || ''}
                          onChange={(e) => {
                            const updated = [...listing.nearby_places];
                            updated[index] = { ...place, latitude: parseFloat(e.target.value) || null };
                            setListing({ ...listing, nearby_places: updated });
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="e.g., 77.5946"
                          value={place.longitude || ''}
                          onChange={(e) => {
                            const updated = [...listing.nearby_places];
                            updated[index] = { ...place, longitude: parseFloat(e.target.value) || null };
                            setListing({ ...listing, nearby_places: updated });
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Google Maps URL</Label>
                        <Input
                          placeholder="Paste Maps link"
                          value={place.google_maps_url || ''}
                          onChange={(e) => {
                            const updated = [...listing.nearby_places];
                            updated[index] = { ...place, google_maps_url: e.target.value };
                            setListing({ ...listing, nearby_places: updated });
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </TabsContent>

      {/* Amenities Tab */}
      <TabsContent value="amenities" className="space-y-6 mt-4">
        {/* Hotel-Level Amenities */}
        <div className="space-y-4">
          <div>
            <h3 className="font-medium flex items-center gap-2">
              <Building className="h-4 w-4 text-orange-500" />
              Hotel Amenities
            </h3>
            <p className="text-sm text-gray-500">Select amenities offered at the property level</p>
          </div>

          {availableAmenities.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed" data-testid="no-amenities-msg">
              <CheckCircle className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No amenities configured</p>
              <p className="text-xs text-gray-400">Add amenities from Master Data settings first</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="hotel-amenities-grid">
              {availableAmenities.map((amenity) => {
                const isSelected = (listing.amenities || []).includes(amenity.name);
                return (
                  <button
                    key={amenity.id}
                    data-testid={`hotel-amenity-${amenity.id}`}
                    onClick={() => toggleHotelAmenity(amenity.name)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors text-left ${
                      isSelected
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <CheckCircle className={`h-4 w-4 shrink-0 ${isSelected ? 'text-orange-500' : 'text-gray-300'}`} />
                    {amenity.name}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-400">{(listing.amenities || []).length} selected</p>
        </div>

        {/* Room-Type-Level Amenities */}
        <div className="space-y-4 pt-4 border-t">
          <div>
            <h3 className="font-medium flex items-center gap-2">
              <Bed className="h-4 w-4 text-orange-500" />
              Room Type Amenities
            </h3>
            <p className="text-sm text-gray-500">Select amenities specific to each room type</p>
          </div>

          {listing.room_types?.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No room types found</p>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {listing.room_types?.map((rt) => (
                <AccordionItem key={rt.id} value={`amenity-${rt.id}`} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full mr-4">
                      <span className="font-medium">{rt.name}</span>
                      <Badge variant="outline">
                        {(rt.amenities || []).length} amenities
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    {availableAmenities.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-3">No amenities available</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2" data-testid={`room-amenities-grid-${rt.id}`}>
                        {availableAmenities.map((amenity) => {
                          const isSelected = (rt.amenities || []).includes(amenity.name);
                          return (
                            <button
                              key={amenity.id}
                              data-testid={`room-amenity-${rt.id}-${amenity.id}`}
                              onClick={() => toggleRoomAmenity(rt.id, amenity.name)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors text-left ${
                                isSelected
                                  ? 'border-orange-500 bg-orange-50 text-orange-700'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
                              }`}
                            >
                              <CheckCircle className={`h-4 w-4 shrink-0 ${isSelected ? 'text-orange-500' : 'text-gray-300'}`} />
                              {amenity.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </TabsContent>

      {/* Photos Tab */}
      <TabsContent value="photos" className="space-y-6 mt-4">
        {/* Hotel Photos */}
        <div className="space-y-4">
          <div>
            <h3 className="font-medium flex items-center gap-2">
              <Building className="h-4 w-4 text-orange-500" />
              Hotel Photos
            </h3>
            <p className="text-sm text-gray-500">Upload photos to display on the portal property page</p>
          </div>

          {/* Upload button */}
          <div className="flex gap-2">
            <Button
              data-testid="hotel-photo-upload-btn"
              onClick={() => document.getElementById('hotel-photo-input').click()}
              disabled={uploadingHotelPhoto}
              variant="outline"
              className="gap-2"
            >
              {uploadingHotelPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploadingHotelPhoto ? 'Uploading...' : 'Upload Photos'}
            </Button>
            <input
              id="hotel-photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleHotelPhotoUpload}
            />
            <p className="text-xs text-gray-400 self-center">JPEG, PNG, WebP — max 10MB each</p>
          </div>

          {/* Photo grid */}
          {listing.listing_photos?.length > 0 ? (
            <div className="grid grid-cols-4 gap-3">
              {listing.listing_photos.map((photo, index) => (
                <div key={index} className="relative group aspect-video rounded-lg overflow-hidden bg-gray-100" data-testid={`hotel-photo-${index}`}>
                  <img
                    src={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                    alt={`Hotel photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/400x300?text=Image+Error';
                    }}
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeHotelPhoto(index)}
                    data-testid={`remove-hotel-photo-${index}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {index === 0 && (
                    <Badge className="absolute bottom-2 left-2 bg-orange-500">Main</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed" data-testid="no-hotel-photos">
              <Image className="h-10 w-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No hotel photos added</p>
              <p className="text-xs text-gray-400">Upload images to showcase your property</p>
            </div>
          )}
        </div>

        {/* Room Type Photos */}
        <div className="space-y-4 pt-4 border-t">
          <div>
            <h3 className="font-medium flex items-center gap-2">
              <Bed className="h-4 w-4 text-orange-500" />
              Room Type Photos
            </h3>
            <p className="text-sm text-gray-500">Upload photos for each room type</p>
          </div>

          {listing.room_types?.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No room types found</p>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {listing.room_types?.map((rt) => (
                <AccordionItem key={rt.id} value={`photos-${rt.id}`} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full mr-4">
                      <span className="font-medium">{rt.name}</span>
                      <Badge variant="outline">
                        {rt.photos?.length || 0} photos
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    {/* Upload button for room type */}
                    <div className="flex gap-2 mb-3">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`room-photo-upload-btn-${rt.id}`}
                        onClick={() => document.getElementById(`room-photo-input-${rt.id}`).click()}
                        disabled={uploadingRoomPhoto === rt.id}
                        className="gap-2"
                      >
                        {uploadingRoomPhoto === rt.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                        {uploadingRoomPhoto === rt.id ? 'Uploading...' : 'Upload'}
                      </Button>
                      <input
                        id={`room-photo-input-${rt.id}`}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        className="hidden"
                        onChange={(e) => handleRoomPhotoUpload(e, rt.id)}
                      />
                    </div>

                    {/* Room photos grid */}
                    {rt.photos?.length > 0 ? (
                      <div className="grid grid-cols-4 gap-2">
                        {rt.photos.map((photo, photoIdx) => (
                          <div key={photoIdx} className="relative group aspect-video rounded overflow-hidden bg-gray-100" data-testid={`room-photo-${rt.id}-${photoIdx}`}>
                            <img
                              src={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                              alt={`${rt.name} photo ${photoIdx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://placehold.co/400x300?text=Error';
                              }}
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeRoomTypePhoto(rt.id, photoIdx)}
                              data-testid={`remove-room-photo-${rt.id}-${photoIdx}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-4">No photos for this room type</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </TabsContent>

      {/* Room Allocation Tab */}
      <TabsContent value="rooms" className="mt-4">
        {renderRoomAllocationTab()}
      </TabsContent>

      {/* Room Pricing Tab - Per room type pricing controls */}
      <TabsContent value="pricing" className="space-y-4 mt-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium">Room Type Pricing Rules</h3>
            <p className="text-sm text-gray-500">Only room types with active pricing rules appear on the portal</p>
          </div>
        </div>
        
        {loadingPropertyPricing ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Existing Pricing Rules Table */}
            {propertyPricingRules.length > 0 ? (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Room Type</TableHead>
                      <TableHead className="text-xs text-right">Base Price</TableHead>
                      <TableHead className="text-xs text-center">Date Range</TableHead>
                      <TableHead className="text-xs text-center">Max Discount %</TableHead>
                      <TableHead className="text-xs text-center">Undercut %</TableHead>
                      <TableHead className="text-xs text-center">Pricing Source</TableHead>
                      <TableHead className="text-xs text-center">Status</TableHead>
                      <TableHead className="text-xs text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {propertyPricingRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="text-sm font-medium">{rule.name}</TableCell>
                        <TableCell className="text-sm">{rule.room_type_name || 'Any'}</TableCell>
                        <TableCell className="text-sm text-right font-semibold text-green-600">₹{rule.base_price?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-center">
                          {rule.start_date && rule.end_date ? (
                            <div className="text-xs">
                              <div className="flex items-center gap-1 justify-center">
                                <Input
                                  type="date"
                                  className="w-28 h-6 text-xs"
                                  defaultValue={rule.start_date}
                                  onBlur={(e) => {
                                    if (e.target.value && rule.end_date && e.target.value > rule.end_date) {
                                      toast.error('Start must be before end');
                                      return;
                                    }
                                    handleUpdatePricingRule(rule.id, { start_date: e.target.value || null });
                                  }}
                                />
                                <span>-</span>
                                <Input
                                  type="date"
                                  className="w-28 h-6 text-xs"
                                  defaultValue={rule.end_date}
                                  onBlur={(e) => {
                                    if (e.target.value && rule.start_date && e.target.value < rule.start_date) {
                                      toast.error('End must be after start');
                                      return;
                                    }
                                    handleUpdatePricingRule(rule.id, { end_date: e.target.value || null });
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Default</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            max="80"
                            className="w-16 h-7 text-xs text-center mx-auto"
                            defaultValue={rule.max_discount_percent ?? ''}
                            placeholder="Def"
                            onBlur={(e) => {
                              const val = e.target.value ? parseFloat(e.target.value) : null;
                              if (val !== null && (val < 0 || val > 80)) {
                                toast.error('Max discount: 0-80%');
                                return;
                              }
                              handleUpdatePricingRule(rule.id, { max_discount_percent: val });
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            className="w-16 h-7 text-xs text-center mx-auto"
                            defaultValue={rule.competitor_undercut_percent ?? ''}
                            placeholder="Def"
                            onBlur={(e) => {
                              const val = e.target.value ? parseFloat(e.target.value) : null;
                              if (val !== null && (val < 0 || val > 50)) {
                                toast.error('Undercut: 0-50%');
                                return;
                              }
                              handleUpdatePricingRule(rule.id, { competitor_undercut_percent: val });
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Select
                            defaultValue={rule.pricing_source || 'default'}
                            onValueChange={(val) => {
                              handleUpdatePricingRule(rule.id, { pricing_source: val === 'default' ? null : val });
                            }}
                          >
                            <SelectTrigger className="w-28 h-7 text-xs mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Default</SelectItem>
                              <SelectItem value="local">Locally Defined</SelectItem>
                              <SelectItem value="mock">AI Estimated</SelectItem>
                              <SelectItem value="live">Live Search</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            className={`cursor-pointer ${rule.is_active ? 'bg-green-500' : 'bg-gray-400'}`}
                            onClick={() => handleUpdatePricingRule(rule.id, { is_active: !rule.is_active })}
                          >
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 text-red-500"
                            onClick={() => handleDeletePricingRule(rule.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                <p className="text-gray-500">No pricing rules configured</p>
                <p className="text-sm text-gray-400 mt-1">Add pricing rules to show room types on the portal</p>
              </div>
            )}

            {/* Add New Pricing Rule */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-sm mb-3">Add New Pricing Rule</h4>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div>
                  <Label className="text-xs">Rule Name *</Label>
                  <Input
                    id="new-rule-name"
                    placeholder="e.g., Deluxe Room"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Room Type *</Label>
                  <Select onValueChange={(val) => document.getElementById('new-rule-type').value = val}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {roomTypes.map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" id="new-rule-type" />
                </div>
                <div>
                  <Label className="text-xs">Base Price (₹) *</Label>
                  <Input
                    id="new-rule-price"
                    type="number"
                    placeholder="0"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    id="new-rule-start-date"
                    type="date"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">End Date</Label>
                  <Input
                    id="new-rule-end-date"
                    type="date"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const name = document.getElementById('new-rule-name').value;
                      const roomTypeId = document.getElementById('new-rule-type').value;
                      const basePrice = parseFloat(document.getElementById('new-rule-price').value);
                      const startDate = document.getElementById('new-rule-start-date').value || null;
                      const endDate = document.getElementById('new-rule-end-date').value || null;
                      
                      if (!name || !roomTypeId || !basePrice) {
                        toast.error('Fill in name, room type, and price');
                        return;
                      }
                      
                      // Validate date range if both provided
                      if (startDate && endDate && startDate > endDate) {
                        toast.error('Start date must be before or equal to end date');
                        return;
                      }
                      
                      handleCreatePricingRule({
                        name,
                        room_type_id: roomTypeId,
                        base_price: basePrice,
                        start_date: startDate,
                        end_date: endDate,
                        is_active: true
                      });
                      
                      // Clear form
                      document.getElementById('new-rule-name').value = '';
                      document.getElementById('new-rule-type').value = '';
                      document.getElementById('new-rule-price').value = '';
                      document.getElementById('new-rule-start-date').value = '';
                      document.getElementById('new-rule-end-date').value = '';
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Rule
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Leave dates empty for default pricing (applies when no date-specific rule matches)</p>
            </div>
          </div>
        )}
      </TabsContent>

      {/* Discounts Tab */}
      <TabsContent value="discounts" className="space-y-4 mt-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium">Discount Offers</h3>
            <p className="text-sm text-gray-500">Create time-limited discount offers</p>
          </div>
          <Button size="sm" onClick={addDiscountOffer}>
            <Plus className="h-4 w-4 mr-1" />
            Add Offer
          </Button>
        </div>

        {(!listing.discount_offers || listing.discount_offers.length === 0) ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Tag className="h-10 w-10 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No discount offers</p>
          </div>
        ) : (
          <div className="space-y-3">
            {listing.discount_offers.map((offer, index) => (
              <Card key={offer.id || index}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 mr-4">
                      <Label>Offer Name</Label>
                      <Input
                        value={offer.name || ''}
                        onChange={(e) => updateDiscountOffer(offer.id, 'name', e.target.value)}
                        placeholder="e.g., Summer Sale"
                        className="mt-1"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => removeDiscountOffer(offer.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Discount %</Label>
                      <div className="flex items-center mt-1">
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          value={offer.discount_percent || ''}
                          onChange={(e) => updateDiscountOffer(offer.id, 'discount_percent', parseInt(e.target.value) || 0)}
                        />
                        <Percent className="h-4 w-4 ml-1 text-gray-400" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Start Date</Label>
                      <Input
                        type="date"
                        value={offer.start_date || ''}
                        onChange={(e) => updateDiscountOffer(offer.id, 'start_date', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">End Date</Label>
                      <Input
                        type="date"
                        value={offer.end_date || ''}
                        onChange={(e) => updateDiscountOffer(offer.id, 'end_date', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Min. Nights</Label>
                      <Input
                        type="number"
                        min={1}
                        value={offer.min_nights || 1}
                        onChange={(e) => updateDiscountOffer(offer.id, 'min_nights', parseInt(e.target.value) || 1)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Hotel Admin - Single Property View
  if (user?.role === 'hotel_admin') {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Globe className="h-6 w-6 text-orange-500" />
              Online Listing Settings
            </h1>
            <p className="text-gray-500 mt-1">Configure portal listing, room allocations, and discounts</p>
          </div>
          <div className="flex items-center gap-3">
            {listing.listing_enabled && (
              <Button variant="outline" asChild>
                <a href={`/portal/property/${hotelId}`} target="_blank" rel="noopener noreferrer">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </a>
              </Button>
            )}
            <Button onClick={handleSaveListing} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {listing.listing_enabled ? (
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                ) : (
                  <div className="p-3 bg-gray-100 rounded-full">
                    <XCircle className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-medium text-lg">Portal Listing Status</h3>
                  <p className="text-gray-500">
                    {listing.listing_enabled ? 'Property is visible on portal' : 'Property is hidden from portal'}
                  </p>
                </div>
              </div>
              <Switch
                checked={listing.listing_enabled}
                onCheckedChange={(checked) => setListing({ ...listing, listing_enabled: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {listing.listing_enabled && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-orange-500" />
                Listing Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderConfigureDialogContent()}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Super Admin / Company Admin - List View
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-orange-500" />
            Portal Properties
          </h1>
          <p className="text-gray-500 mt-1">Manage properties on the public booking portal</p>
        </div>
        <Badge variant="outline" className="text-base px-3 py-1">
          {properties.filter(p => p.listing?.listing_enabled).length} / {properties.length} Active
        </Badge>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Property</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold text-center">Rooms</TableHead>
                <TableHead className="font-semibold text-center">Max Discount %</TableHead>
                <TableHead className="font-semibold text-center">Undercut %</TableHead>
                <TableHead className="font-semibold text-center">Pricing Source</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
                <TableHead className="font-semibold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProperties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No properties found
                  </TableCell>
                </TableRow>
              ) : (
                filteredProperties.map((property) => (
                  <TableRow key={property.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                          <Building className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium">{property.name}</p>
                          <p className="text-xs text-gray-500">{property.property_type || 'Hotel'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="text-sm">{property.city || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{property.total_rooms}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="80"
                          className="w-16 h-8 text-center text-sm"
                          defaultValue={property.max_portal_discount || 10}
                          data-testid={`max-discount-${property.id}`}
                          onBlur={async (e) => {
                            const val = parseInt(e.target.value) || 10;
                            try {
                              const res = await api.put(`/dynamic-pricing/max-discount/${property.id}`, { max_portal_discount: val });
                              if (res.data?.message) {
                                toast.success(`Discount set to ${val}%`);
                              }
                            } catch (err) {
                              console.error('Discount update error:', err?.response?.data || err.message);
                              toast.error(err?.response?.data?.detail || 'Failed to update discount');
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                          }}
                        />
                        <span className="text-xs text-gray-500">%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          className="w-16 h-8 text-center text-sm"
                          defaultValue={property.competitor_undercut_percent || 5}
                          data-testid={`undercut-percent-${property.id}`}
                          onBlur={async (e) => {
                            const val = parseInt(e.target.value) || 5;
                            try {
                              await api.put(`/dynamic-pricing/pricing-settings/${property.id}`, { competitor_undercut_percent: val });
                              toast.success(`Undercut set to ${val}%`);
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Failed to update');
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                          }}
                        />
                        <span className="text-xs text-gray-500">%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Select
                        defaultValue={property.pricing_source || 'mock'}
                        onValueChange={async (val) => {
                          try {
                            await api.put(`/dynamic-pricing/pricing-settings/${property.id}`, { pricing_source: val });
                            const labels = { mock: 'AI Estimated', live: 'Live Web Search', local: 'Locally Defined' };
                            toast.success(`Pricing source set to ${labels[val] || val}`);
                            // Update local state
                            setProperties(prev => prev.map(p => 
                              p.id === property.id ? { ...p, pricing_source: val } : p
                            ));
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Failed to update pricing source');
                          }
                        }}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`pricing-source-${property.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">Locally Defined</SelectItem>
                          <SelectItem value="mock">AI Estimated</SelectItem>
                          <SelectItem value="live">Live Search</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      {property.listing?.listing_enabled ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100">
                          <XCircle className="h-3 w-3 mr-1" />Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant={property.listing?.listing_enabled ? "outline" : "default"}
                          onClick={() => togglePropertyListing(property)}
                          disabled={togglingProperty === property.id}
                          className={property.listing?.listing_enabled 
                            ? "border-red-200 text-red-600 hover:bg-red-50" 
                            : "bg-green-600 hover:bg-green-700"}
                        >
                          {togglingProperty === property.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : property.listing?.listing_enabled ? (
                            <><ToggleRight className="h-4 w-4 mr-1" />Deactivate</>
                          ) : (
                            <><ToggleLeft className="h-4 w-4 mr-1" />Activate</>
                          )}
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`configure-btn-${property.id}`} onClick={() => {
                          openTab({
                            type: TAB_TYPES.PAGE_PROPERTY_CONFIGURE,
                            id: property.id,
                            title: `Configure: ${property.name}`,
                            data: { propertyId: property.id, propertyName: property.name }
                          });
                        }}>
                          <Edit className="h-4 w-4 mr-1" />Configure
                        </Button>
                        <Button size="sm" variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50" onClick={() => openPricingAnalysis(property)} data-testid={`pricing-analysis-btn-${property.id}`}>
                          <BarChart3 className="h-4 w-4 mr-1" />Pricing
                        </Button>
                        {property.listing?.listing_enabled && (
                          <Button size="sm" variant="ghost" asChild>
                            <a href={`/portal/property/${property.id}`} target="_blank" rel="noopener noreferrer">
                              <Eye className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Configure Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          {/* Property Header - Sticky */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Building className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{selectedProperty?.name}</h2>
                  <p className="text-orange-100 text-sm">
                    {selectedProperty?.city}{selectedProperty?.state ? `, ${selectedProperty.state}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => {
                    setEditDialogOpen(false);
                    openTab({
                      type: TAB_TYPES.PAGE_PROPERTY_CONFIGURE,
                      id: selectedProperty?.id,
                      title: `Configure: ${selectedProperty?.name}`,
                      data: { propertyId: selectedProperty?.id, propertyName: selectedProperty?.name }
                    });
                  }}
                >
                  <Globe className="h-4 w-4 mr-1" />
                  Open in Tab
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loadingListing ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
              </div>
            ) : (
              renderConfigureDialogContent()
            )}
          </div>

          {/* Footer - Sticky */}
          <div className="sticky bottom-0 border-t bg-white px-6 py-4 rounded-b-lg">
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveListing} disabled={saving || loadingListing}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Competitive Analysis Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Competitive Analysis — {pricingProperty?.name}
            </DialogTitle>
            <DialogDescription>
              AI-powered competitor price analysis across major booking platforms
            </DialogDescription>
          </DialogHeader>

          {loadingPricing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
              <p className="text-gray-500">Analyzing competitor prices across platforms...</p>
              <p className="text-xs text-gray-400 mt-1">This may take a few seconds</p>
            </div>
          ) : pricingData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No room types configured for this property</p>
            </div>
          ) : (
            <div className="space-y-6">
              {pricingData.map((data, idx) => (
                <div key={idx} className="border rounded-lg p-4" data-testid={`pricing-card-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{data.roomType}</h3>
                      <p className="text-sm text-gray-500">Our base price: <span className="font-medium text-gray-700">₹{data.basePrice?.toLocaleString('en-IN')}</span></p>
                    </div>
                    <div className="text-right">
                      {data.lowest_competitor && (
                        <div className="flex items-center gap-1">
                          {data.base_price <= data.lowest_competitor ? (
                            <Badge className="bg-green-100 text-green-700 gap-1"><TrendingDown className="h-3 w-3" />We're cheapest</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 gap-1"><TrendingUp className="h-3 w-3" />Below market</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {data.competitors?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Platform</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">Price / Night</th>
                            <th className="text-right px-3 py-2 font-medium text-gray-600">vs Our Base</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.competitors.map((comp, ci) => {
                            const diff = comp.price_per_night - data.basePrice;
                            const diffPercent = data.basePrice > 0 ? ((diff / data.basePrice) * 100).toFixed(1) : 0;
                            const isLowest = comp.price_per_night === data.lowest_competitor;
                            return (
                              <tr key={ci} className={`border-b last:border-0 ${isLowest ? 'bg-red-50' : ''}`}>
                                <td className="px-3 py-2 font-medium">
                                  {comp.platform}
                                  {isLowest && <Badge className="ml-2 bg-red-100 text-red-700 text-[10px] px-1 py-0">Lowest</Badge>}
                                </td>
                                <td className={`px-3 py-2 text-right ${isLowest ? 'font-bold text-red-600' : ''}`}>
                                  ₹{comp.price_per_night?.toLocaleString('en-IN')}
                                </td>
                                <td className={`px-3 py-2 text-right font-medium ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                  {diff > 0 ? '+' : ''}{diffPercent}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">{data.error ? 'Failed to fetch' : 'No competitor data'}</p>
                  )}

                  {/* Our AI Recommended Price */}
                  {data.recommended_portal_price && (
                    <div className="mt-3 grid grid-cols-3 gap-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                      <div className="text-center">
                        <p className="text-[10px] uppercase text-gray-500 font-medium">Lowest Competitor</p>
                        <p className="text-lg font-bold text-red-600">₹{data.lowest_competitor?.toLocaleString('en-IN')}</p>
                      </div>
                      <div className="text-center border-x border-orange-200">
                        <p className="text-[10px] uppercase text-gray-500 font-medium">Our AI Price</p>
                        <p className="text-lg font-bold text-orange-600">₹{data.recommended_portal_price?.toLocaleString('en-IN')}</p>
                        <Badge className="bg-green-600 text-white text-[10px]">{data.discount_percent}% OFF</Badge>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] uppercase text-gray-500 font-medium">Savings for Guest</p>
                        <p className="text-lg font-bold text-green-600">₹{(data.basePrice - data.recommended_portal_price)?.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                  )}

                  {data.market_analysis && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800"><span className="font-medium">AI Insight:</span> {data.market_analysis}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyListing;
